import { devWarn, devError } from './devLog';
/**
 * Avatar Caching Utility
 * Caches user avatars to avoid hitting Google API rate limits
 */

const CACHE_NAME = 'hora-avatar-cache';
const CACHE_EXPIRY_DAYS = 7; // Cache for 7 days

interface CachedAvatar {
    url: string;
    dataUrl: string;
    timestamp: number;
}

/**
 * Check if cached avatar is still valid
 */
function isCacheValid(timestamp: number): boolean {
    const now = Date.now();
    const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000; // 7 days in ms
    return (now - timestamp) < expiryTime;
}

/**
 * Fetch and cache avatar image
 */
export async function cacheAvatar(url: string): Promise<string> {
    try {
        // Check if we already have it cached
        const cached = await getCachedAvatar(url);
        if (cached) {
            return cached;
        }

        // Fetch the image
        const response = await fetch(url, {
            referrerPolicy: 'no-referrer',
            mode: 'cors'
        });

        if (!response.ok) {
            devWarn('Failed to fetch avatar:', response.status);
            return url; // Return original URL on error
        }

        // Convert to blob
        const blob = await response.blob();

        // Convert blob to data URL
        const dataUrl = await blobToDataUrl(blob);

        // Store in localStorage
        const cacheData: CachedAvatar = {
            url,
            dataUrl,
            timestamp: Date.now()
        };

        try {
            localStorage.setItem(`${CACHE_NAME}:${url}`, JSON.stringify(cacheData));
        } catch (e) {
            devWarn('Failed to cache avatar in localStorage:', e);
            // If storage is full, clear old cache
            clearExpiredCache();
        }

        return dataUrl;
    } catch (error) {
        devError('Error caching avatar:', error);
        return url; // Return original URL on error
    }
}

/**
 * Get cached avatar if available and valid
 */
export async function getCachedAvatar(url: string): Promise<string | null> {
    try {
        const cacheKey = `${CACHE_NAME}:${url}`;
        const cached = localStorage.getItem(cacheKey);

        if (!cached) {
            return null;
        }

        const cacheData: CachedAvatar = JSON.parse(cached);

        // Check if cache is still valid
        if (!isCacheValid(cacheData.timestamp)) {
            localStorage.removeItem(cacheKey);
            return null;
        }

        return cacheData.dataUrl;
    } catch (error) {
        devError('Error getting cached avatar:', error);
        return null;
    }
}

/**
 * Convert blob to data URL
 */
function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Clear expired cache entries
 */
export function clearExpiredCache(): void {
    try {
        const keys = Object.keys(localStorage);
        for (const key of keys) {
            if (key.startsWith(CACHE_NAME)) {
                const cached = localStorage.getItem(key);
                if (cached) {
                    const cacheData: CachedAvatar = JSON.parse(cached);
                    if (!isCacheValid(cacheData.timestamp)) {
                        localStorage.removeItem(key);
                    }
                }
            }
        }
    } catch (error) {
        devError('Error clearing expired cache:', error);
    }
}

/**
 * Clear all avatar cache
 */
export function clearAllAvatarCache(): void {
    try {
        const keys = Object.keys(localStorage);
        for (const key of keys) {
            if (key.startsWith(CACHE_NAME)) {
                localStorage.removeItem(key);
            }
        }
    } catch (error) {
        devError('Error clearing avatar cache:', error);
    }
}
